import type { CvEducation, CvPersonal } from '~/composables/useCvEditor'

export type CvApplyMissingField = 'name' | 'phone' | 'education'

function normalizeEducationField(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return String((value as { value: unknown }).value).trim()
  }
  return String(value).trim()
}

export function normalizeCvEducations(educations: unknown): CvEducation[] {
  if (!Array.isArray(educations)) return []

  return educations.map((entry) => {
    if (!entry || typeof entry !== 'object') {
      return { status: '', university: '', is_current: false }
    }

    const raw = entry as Record<string, unknown>
    const level = normalizeEducationField(raw.level)

    return {
      status: normalizeEducationField(raw.status),
      level: level || null,
      faculty_id: typeof raw.faculty_id === 'number' ? raw.faculty_id : null,
      faculty_other: typeof raw.faculty_other === 'string' ? raw.faculty_other : null,
      university: typeof raw.university === 'string' ? raw.university.trim() : normalizeEducationField(raw.university),
      start_date: typeof raw.start_date === 'string' ? raw.start_date : null,
      end_date: typeof raw.end_date === 'string' ? raw.end_date : null,
      is_current: Boolean(raw.is_current),
    }
  })
}

export function isNameCompleteForApply(personal: CvPersonal): boolean {
  return !!personal.full_name?.trim()
}

export function isPhoneCompleteForApply(personal: CvPersonal): boolean {
  return !!personal.whatsapp?.trim()
}

export function isEducationCompleteForApply(educations: CvEducation[]): boolean {
  return normalizeCvEducations(educations).some((education) => {
    if (!education.status || !education.university) return false
    if (education.status === 'graduate' && !education.level) return false
    return true
  })
}

export function getCvMissingFieldsForApply(
  personal: CvPersonal,
  educations: CvEducation[] = [],
): CvApplyMissingField[] {
  const missing: CvApplyMissingField[] = []

  // Phone number is intentionally NOT required to apply / send a CV.
  if (!isNameCompleteForApply(personal)) missing.push('name')
  if (!isEducationCompleteForApply(educations)) missing.push('education')

  return missing
}

/** Minimum CV fields required before a seeker can apply from posted jobs. */
export function isCvReadyForApply(
  personal: CvPersonal,
  educations: CvEducation[] = [],
): boolean {
  return getCvMissingFieldsForApply(personal, educations).length === 0
}

export function formatCvMissingFieldsMessage(
  missing: CvApplyMissingField[],
  t: (key: string, params?: Record<string, string>) => string,
  lang: 'ar' | 'en' = 'en',
): string {
  if (missing.length === 0) {
    return t('jobs.cvApplyAlert')
  }

  if (missing.length === 1) {
    return t(`jobs.cvMissing.${missing[0]}`)
  }

  const fieldLabels = missing.map(field => t(`jobs.cvMissing.field.${field}`))
  const list = new Intl.ListFormat(lang === 'ar' ? 'ar' : 'en', {
    style: 'long',
    type: 'conjunction',
  }).format(fieldLabels)

  return t('jobs.cvMissing.multiple', { fields: list })
}

export function isCvApplyMissingField(value: string): value is CvApplyMissingField {
  return value === 'name' || value === 'phone' || value === 'education'
}
